module.exports = function (Cue) {
    'use strict';
    var isHeadless = /HeadlessChrome/.test(navigator.userAgent);
    function windowIsVisible() {
        if (isHeadless || navigator.webdriver) {
            return false;
        }
        return typeof document === 'object' && 'visibilityState' in document && document.visibilityState === 'visible';
    }
    var requestAnimationFrame = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;
    var cancelAnimationFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame || window.webkitCancelAnimationFrame || window.msCancelAnimationFrame;
    var eventTypes = {
        forward: {},
        reverse: {}
    };
    var EVENT_TYPE_POINT = 0;
    var EVENT_TYPE_SEGMENT_ENTER = 1;
    var EVENT_TYPE_SEGMENT_EXIT = 2;
    eventTypes.forward[Cue.POINT] = EVENT_TYPE_POINT;
    eventTypes.forward[Cue.SEGMENT_START] = EVENT_TYPE_SEGMENT_ENTER;
    eventTypes.forward[Cue.SEGMENT_END] = EVENT_TYPE_SEGMENT_EXIT;
    eventTypes.reverse[Cue.POINT] = EVENT_TYPE_POINT;
    eventTypes.reverse[Cue.SEGMENT_START] = EVENT_TYPE_SEGMENT_EXIT;
    eventTypes.reverse[Cue.SEGMENT_END] = EVENT_TYPE_SEGMENT_ENTER;
    var eventNames = {};
    eventNames[EVENT_TYPE_POINT] = 'points.enter';
    eventNames[EVENT_TYPE_SEGMENT_ENTER] = 'segments.enter';
    eventNames[EVENT_TYPE_SEGMENT_EXIT] = 'segments.exit';
    function getPointOrSegment(peaks, cue) {
        switch (cue.type) {
        case Cue.POINT:
            return peaks.points.getPoint(cue.id);
        case Cue.SEGMENT_START:
        case Cue.SEGMENT_END:
            return peaks.segments.getSegment(cue.id);
        default:
            throw new Error('getPointOrSegment: id not found?');
        }
    }
    function CueEmitter(peaks) {
        this._cues = [];
        this._peaks = peaks;
        this._previousTime = -1;
        this._updateCues = this._updateCues.bind(this);
        this._onPlay = this.onPlay.bind(this);
        this._onSeek = this.onSeek.bind(this);
        this._onTimeUpdate = this.onTimeUpdate.bind(this);
        this._onAnimationFrame = this.onAnimationFrame.bind(this);
        this._rAFHandle = null;
        this._activeSegments = {};
        this._attachEventHandlers();
    }
    CueEmitter.prototype._updateCues = function () {
        var self = this;
        var points = self._peaks.points.getPoints();
        var segments = self._peaks.segments.getSegments();
        self._cues.length = 0;
        points.forEach(function (point) {
            self._cues.push(new Cue(point.time, Cue.POINT, point.id));
        });
        segments.forEach(function (segment) {
            self._cues.push(new Cue(segment.startTime, Cue.SEGMENT_START, segment.id));
            self._cues.push(new Cue(segment.endTime, Cue.SEGMENT_END, segment.id));
        });
        self._cues.sort(Cue.sorter);
        var time = self._peaks.player.getCurrentTime();
        self._updateActiveSegments(time);
    };
    CueEmitter.prototype._onUpdate = function (time, previousTime) {
        var isForward = time > previousTime;
        var start;
        var end;
        var step;
        if (isForward) {
            start = 0;
            end = this._cues.length;
            step = 1;
        } else {
            start = this._cues.length - 1;
            end = -1;
            step = -1;
        }
        for (var i = start; isForward ? i < end : i > end; i += step) {
            var cue = this._cues[i];
            if (isForward ? cue.time > previousTime : cue.time < previousTime) {
                if (isForward ? cue.time > time : cue.time < time) {
                    break;
                }
                var marker = getPointOrSegment(this._peaks, cue);
                var eventType = isForward ? eventTypes.forward[cue.type] : eventTypes.reverse[cue.type];
                if (eventType === EVENT_TYPE_SEGMENT_ENTER) {
                    this._activeSegments[marker.id] = marker;
                } else if (eventType === EVENT_TYPE_SEGMENT_EXIT) {
                    delete this._activeSegments[marker.id];
                }
                this._peaks.emit(eventNames[eventType], marker);
            }
        }
    };
    CueEmitter.prototype.onTimeUpdate = function (time) {
        if (windowIsVisible()) {
            return;
        }
        if (this._peaks.player.isPlaying() && !this._peaks.player.isSeeking()) {
            this._onUpdate(time, this._previousTime);
        }
        this._previousTime = time;
    };
    CueEmitter.prototype.onAnimationFrame = function () {
        var time = this._peaks.player.getCurrentTime();
        if (!this._peaks.player.isSeeking()) {
            this._onUpdate(time, this._previousTime);
        }
        this._previousTime = time;
        if (this._peaks.player.isPlaying()) {
            this._rAFHandle = requestAnimationFrame(this._onAnimationFrame);
        }
    };
    CueEmitter.prototype.onPlay = function () {
        this._previousTime = this._peaks.player.getCurrentTime();
        this._rAFHandle = requestAnimationFrame(this._onAnimationFrame);
    };
    CueEmitter.prototype.onSeek = function (time) {
        this._previousTime = time;
        this._updateActiveSegments(time);
    };
    function getSegmentIdComparator(id) {
        return function compareSegmentIds(segment) {
            return segment.id === id;
        };
    }
    CueEmitter.prototype._updateActiveSegments = function (time) {
        var self = this;
        var activeSegments = self._peaks.segments.getSegmentsAtTime(time);
        for (var id in self._activeSegments) {
            if (Object.prototype.hasOwnProperty.call(self._activeSegments, id)) {
                var segment = activeSegments.find(getSegmentIdComparator(id));
                if (!segment) {
                    self._peaks.emit('segments.exit', self._activeSegments[id]);
                    delete self._activeSegments[id];
                }
            }
        }
        activeSegments.forEach(function (segment) {
            if (!(segment.id in self._activeSegments)) {
                self._activeSegments[segment.id] = segment;
                self._peaks.emit('segments.enter', segment);
            }
        });
    };
    var triggerUpdateOn = Array('points.update', 'points.dragmove', 'points.add', 'points.remove', 'points.remove_all', 'segments.update', 'segments.dragged', 'segments.add', 'segments.remove', 'segments.remove_all');
    CueEmitter.prototype._attachEventHandlers = function () {
        this._peaks.on('player_time_update', this._onTimeUpdate);
        this._peaks.on('player_play', this._onPlay);
        this._peaks.on('player_seek', this._onSeek);
        for (var i = 0; i < triggerUpdateOn.length; i++) {
            this._peaks.on(triggerUpdateOn[i], this._updateCues);
        }
        this._updateCues();
    };
    CueEmitter.prototype._detachEventHandlers = function () {
        this._peaks.off('player_time_update', this._onTimeUpdate);
        this._peaks.off('player_play', this._onPlay);
        this._peaks.off('player_seek', this._onSeek);
        for (var i = 0; i < triggerUpdateOn.length; i++) {
            this._peaks.off(triggerUpdateOn[i], this._updateCues);
        }
    };
    CueEmitter.prototype.destroy = function () {
        if (this._rAFHandle) {
            cancelAnimationFrame(this._rAFHandle);
            this._rAFHandle = null;
        }
        this._detachEventHandlers();
        this._previousTime = -1;
    };
    return CueEmitter;
}(require('peaks/cues/cue'));